import { useEffect, useState } from 'react'
import type { DependencyList } from 'react'

type AsyncState<T> = {
  data: T | null
  error: Error | null
  loading: boolean
  reload: () => void
}

export function useAsyncData<T>(loader: () => Promise<T>, deps: DependencyList = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [loading, setLoading] = useState(true)
  const [version, setVersion] = useState(0)

  useEffect(() => {
    let cancelled = false

    setLoading(true)
    setError(null)

    loader()
      .then((result) => {
        if (!cancelled) {
          setData(result)
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setError(error instanceof Error ? error : new Error('Failed to load data'))
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
    // The caller supplies the data-loading key through deps; loader closures are intentionally scoped to that key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, version])

  return {
    data,
    error,
    loading,
    reload: () => setVersion((current) => current + 1),
  }
}
